import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Droplets,
  Sparkles,
  Wind,
  Shield,
  Wrench,
  Zap,
  Clock,
  Check,
} from 'lucide-react';
import GlassCard from '../components/GlassCard';
import { services } from '../data/services';

const ServicesPage = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedService, setSelectedService] = useState(null);

  const content = {
    title: isAr ? 'خدماتنا' : 'Our Services',
    subtitle: isAr
      ? 'خدمات غسيل وتلميع السيارات المتنقلة المتميزة في الكويت'
      : 'Premium mobile car wash and detailing services in Kuwait',
    includes: isAr ? 'تتضمن الخدمة:' : 'Service Includes:',
    starting: isAr ? 'يبدأ من' : 'Starting from',
    currency: isAr ? 'د.ك' : 'KD',
    bookBtn: isAr ? 'احجز الآن' : 'Book Now',
    whyTitle: isAr ? 'لماذا تختار خدماتنا؟' : 'Why Choose Our Services?',
    ctaTitle: isAr
      ? 'جاهز لغسيل سيارات متميز؟'
      : 'Ready for a Premium Car Wash?',
    ctaDesc: isAr
      ? 'احجز موعدك الآن واختبر الفرق مع سوبر كلاسيكي.'
      : 'Book your appointment now and experience the Super Classy difference.',
    ctaBtn: isAr ? 'احجز موعدك الآن' : 'Book Appointment Now',
  };

  // ✅ Added missing benefits array
  const benefits = isAr
    ? [
        'معدات ذات جودة احترافية',
        'منتجات صديقة للبيئة',
        'فنيون معتمدون ومختصون',
        'تقنية توفير المياه',
        'خدمة متنقلة تصل لموقعك',
        'وقت إنجاز سريع',
        'أسعار تنافسية',
        'ضمان الرضا التام',
      ]
    : [
        'Professional-grade equipment',
        'Eco-friendly products',
        'Certified technicians',
        'Water-saving technology',
        'Mobile service at your location',
        'Quick turnaround time',
        'Affordable pricing',
        'Satisfaction guarantee',
      ];

  const serviceIcons = {
    Waxing: <Sparkles className='h-6 w-6 text-white' />,
    'تلميع واكس': <Sparkles className='h-6 w-6 text-white' />,
    Polishing: <Zap className='h-6 w-6 text-white' />,
    'بوليش (تلميع خارجي)': <Zap className='h-6 w-6 text-white' />,
    'Leather Seat Treatment': <Shield className='h-6 w-6 text-white' />,
    'عناية الكراسي الجلد': <Shield className='h-6 w-6 text-white' />,
    'Window Polishing': <Droplets className='h-6 w-6 text-white' />,
    'بوليش زجاج': <Droplets className='h-6 w-6 text-white' />,
    'Engine Cleaning': <Wrench className='h-6 w-6 text-white' />,
    'تنظيف محرك (مكينة)': <Wrench className='h-6 w-6 text-white' />,
    'Stains Removal': <Wind className='h-6 w-6 text-white' />,
    'إزالة البقع المستعصية': <Wind className='h-6 w-6 text-white' />,
    'AC Sanitizing': <Droplets className='h-6 w-6 text-white' />,
    'تعقيم التكييف': <Droplets className='h-6 w-6 text-white' />,
  };

  const openWhatsApp = (serviceName) => {
    const message = encodeURIComponent(
      isAr
        ? `مرحباً، أود حجز خدمة ${serviceName}.`
        : `Hi, I want to book ${serviceName} service.`,
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  return (
    <div className='pt-32' dir={isAr ? 'rtl' : 'ltr'}>
      <div className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className='text-center mb-12'
          >
            <h1 className='text-4xl md:text-6xl font-black mb-4 text-gradient'>
              {content.title}
            </h1>
            <p className='text-lg text-gray-400 max-w-2xl mx-auto'>
              {content.subtitle}
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20'>
            {services.map((service, index) => {
              const sName = isAr
                ? service.nameAr || service.name
                : service.name;
              const sDesc = isAr
                ? service.descriptionAr || service.description
                : service.description;

              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() =>
                    setSelectedService(
                      selectedService?.id === service.id ? null : service,
                    )
                  }
                  className='cursor-pointer group'
                >
                  <GlassCard className='p-6 h-full flex flex-col border border-white/5 bg-white/5 hover:bg-white/10 transition-all'>
                    <div className='relative mb-6'>
                      <div
                        className={`inline-flex p-3 premium-gradient rounded-xl relative z-10 shadow-lg ${isAr ? 'ml-auto' : ''}`}
                      >
                        {serviceIcons[service.name] || (
                          <Sparkles className='h-6 w-6 text-white' />
                        )}
                      </div>
                      <div className='absolute top-1/2 left-0 w-full h-10 premium-gradient blur-3xl opacity-20 -translate-y-1/2' />
                    </div>

                    <h3 className='text-xl font-black mb-2 text-white'>
                      {sName}
                    </h3>
                    <p className='text-gray-400 text-sm mb-8 leading-relaxed line-clamp-2'>
                      {sDesc}
                    </p>

                    <div
                      className={`mt-auto pt-4 border-t border-white/10 flex items-center justify-between ${isAr ? 'flex-row-reverse' : ''}`}
                    >
                      <div
                        className={`flex flex-col ${isAr ? 'text-right' : 'text-left'}`}
                      >
                        <span className='text-[10px] font-bold text-gray-500 uppercase tracking-tighter'>
                          {content.starting}
                        </span>
                        <span className='text-lg font-black text-royal-purple-400'>
                          {isAr
                            ? `${service.price} ${content.currency}`
                            : `KD ${service.price}`}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openWhatsApp(sName);
                        }}
                        className='px-6 py-2 premium-gradient text-white rounded-lg text-xs font-black shadow-lg hover:scale-105 active:scale-95 transition-all'
                      >
                        {content.bookBtn}
                      </button>
                    </div>

                    <AnimatePresence>
                      {selectedService?.id === service.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className='mt-6 pt-6 border-t border-white/10'
                        >
                          <ul className='space-y-2'>
                            {(isAr ? service.featuresAr : service.features).map(
                              (f, i) => (
                                <li
                                  key={i}
                                  className={`flex items-center gap-2 text-xs font-medium ${isAr ? 'flex-row-reverse text-right' : ''}`}
                                >
                                  <Check className='h-3 w-3 text-green-500 flex-shrink-0' />
                                  <span className='text-gray-300'>{f}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='mb-20'
          >
            <GlassCard
              className='p-10 border border-white/10 bg-white/5'
              lang={lang}
            >
              <h2 className='text-3xl font-black mb-10 text-center text-gradient'>
                {content.whyTitle}
              </h2>
              <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 ${isAr ? 'flex-row-reverse text-right' : ''}`}
                  >
                    <Check className='h-5 w-5 text-green-500 flex-shrink-0 mt-1' />
                    <span className='text-gray-300 font-bold text-sm leading-relaxed'>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* CTA Section - Center Aligned */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='text-center flex justify-center'
          >
            <GlassCard className='p-12 border border-white/20 shadow-2xl bg-white/5 max-w-4xl w-full flex flex-col items-center text-center'>
              <h2 className='text-4xl font-black mb-4 text-white'>
                {content.ctaTitle}
              </h2>
              <p className='text-gray-400 mb-10 max-w-lg mx-auto font-medium text-lg leading-relaxed'>
                {content.ctaDesc}
              </p>
              <button
                onClick={() => openWhatsApp('Premium Car Wash')}
                className='inline-flex items-center gap-3 px-12 py-5 premium-gradient text-white rounded-full font-black text-xl shadow-2xl hover:shadow-royal-purple-500/40 transition-all active:scale-95'
              >
                <Clock className='h-6 w-6' />
                {content.ctaBtn}
              </button>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
